import React, { useEffect, useRef, useState } from "react";
import * as echarts from "echarts";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useDispatch, useSelector } from "react-redux";
import { fetchStatus } from './../../../../../store/slices/statusSlice';
import { setFilteredReports } from "../../../../../store/slices/filteredDataSlice";
import { useCurrencySymbol } from "../../../../../utils/currencyUtils";
import { ChevronDown } from "lucide-react"
import { hasViewOrFullAccess, getExactFeatureAccessLevel } from "../../../../../utils/featureAccessUtils";

import Sales from "./sales";
import CommaConvert from "../../../../../utils/CommaConvert";
const RevenueDistribution = ({ currentEmployee, hasNoFeatureConfig, userLevel }) => {

    const dispatch = useDispatch();
    const currency = useCurrencySymbol();
    const USAComma = CommaConvert();

    const downloadAccessLevel = !hasNoFeatureConfig && currentEmployee
        ? (
            getExactFeatureAccessLevel(currentEmployee, "REVENUE DISTRIBUTION DOWNLOAD")
            ?? getExactFeatureAccessLevel(currentEmployee, "REVENUE DISTRIBUTION GRAPHS")
        )
        : null;
    const isDownloadViewOnly = downloadAccessLevel && String(downloadAccessLevel).trim().toUpperCase() === "VIEW ONLY";
    const { status } = useSelector((state) => state.status);

    const canShowGraphs = userLevel === "L0" || hasNoFeatureConfig || !currentEmployee || hasViewOrFullAccess(currentEmployee, "REVENUE DISTRIBUTION GRAPHS");

    const [filteredLeadReports, setFilteredLeadReports] = useState([]);
    const { filteredReports } = useSelector((state) => state.filteredData);
    const [showgraph, setShowgraph] = useState(true);
    const [localFilteredReports, setLocalFilteredReports] = useState([]);

    useEffect(() => {
        if (filteredReports) {
            setLocalFilteredReports(filteredReports);
        }
    }, [filteredReports]);

    const handleToggleTable = () => {
        setShowgraph(!showgraph);
    };

    useEffect(() => {
        if (Array.isArray(filteredReports)) {
            const filteredLeads = filteredReports.filter(
                (lead) => lead.Status === "Won" || lead.Status === "Won Recurring"
            );
            setFilteredLeadReports(filteredLeads);
        }
    }, [filteredReports]);

    useEffect(() => {
        dispatch(setFilteredReports());
        dispatch(fetchStatus());
    }, [dispatch]);


    const dropdownRefService = useRef(null);
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                dropdownRefService.current &&
                !dropdownRefService.current.contains(event.target)
            ) {
                setShowStatusDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const userInteracted = useRef(false);

    const [showStatusDropdown, setShowStatusDropdown] = useState("");
    const [TypeStatus, setTypeStatus] = useState("All Status");
    const [statusTypes, setStatuses] = useState([]);
    const [showStatusTypeMessage, setShowStatusTypeMessage] = useState(false);

    useEffect(() => {
        if (status.length > 0) {
            const FilteredStatus = status.filter(
                (status) => status.status === "Won" || status.status === "Won Recurring"
            );
            setStatuses(FilteredStatus);
        }
    }, [status]);

    const handleStatusChange = (status) => {
        const value = status;
        userInteracted.current = true;

        if (status === "All Status") {
            setTypeStatus("All Status"); // Set "All" as selected
        } else {
            setTypeStatus(value); // Otherwise, set selected status
        }
        setShowStatusDropdown(false);
        setShowStatusTypeMessage(false);
    };

    const [meanData, setMeanData] = useState([]);
    const [countN, setCountN] = useState(0);

    useEffect(() => {
        const applyFilters = () => {
            let updatedLeads = [...filteredLeadReports].sort(
                (a, b) => a.QuotedValue - b.QuotedValue
            );

            // If TypeStatus is "All Status", show all leads
            if (TypeStatus !== "All Status") {
                updatedLeads = updatedLeads.filter(
                    (lead) => lead.Status === TypeStatus
                );
            }
            setMeanData(updatedLeads);
            setCountN(updatedLeads.length);
        };
        applyFilters();
    }, [TypeStatus, filteredLeadReports]);

    const [meanValue, setMeanValue] = useState(0);
    const [sdSquareSIGMA, setSdSquareSIGMA] = useState();
    const [resultArray, setResultArray] = useState([]);

    useEffect(() => {
        if (meanValue && sdSquareSIGMA !== undefined) {
            const arr =
                sdSquareSIGMA === 0
                    ? Array(7).fill(meanValue) // If sdSquareSIGMA is 0, return an array of meanValue
                    : [
                        meanValue - 3 * sdSquareSIGMA,
                        meanValue - 2 * sdSquareSIGMA,
                        meanValue - sdSquareSIGMA,
                        meanValue,
                        meanValue + sdSquareSIGMA,
                        meanValue + 2 * sdSquareSIGMA,
                        meanValue + 3 * sdSquareSIGMA,
                    ].map(customRound);

            setResultArray(arr);
        }
    }, [meanValue, sdSquareSIGMA]);

    const customRound = (num) => {
        const decimal = num - Math.floor(num);
        if (decimal <= 0.5) {
            return Math.floor(num);
        } else {
            return Math.ceil(num);
        }
    };

    useEffect(() => {
        if (meanData.length === 0) {
            setMeanValue(0);
            setSdSquareSIGMA(0);
            setResultArray([]);
            return;
        }

        const total = meanData.reduce((acc, curr) => {
            return acc + Number(curr.QuotedValue); // convert to number
        }, 0);
        const Mean = customRound(total / countN);
        setMeanValue(Mean);

        const S1 = customRound(countN / 2);
        const S2 = customRound(countN / 2 + 1);

        const index1 = S1 - 1;
        const index2 = S2 - 1;

        const valueS1 = meanData[index1]?.QuotedValue || 0;
        const valueS2 = meanData[index2]?.QuotedValue || 0;

        const median = customRound((valueS1 + valueS2) / 2);

        const quotedValuesArray = meanData.map((item) => Number(item.QuotedValue));

        const medianMinusArray = quotedValuesArray.map((value) =>
            customRound(value - median)
        );

        const squaredArray = medianMinusArray.map((num) => num * num);

        const totalSumofSquare = customRound(
            squaredArray.reduce((acc, curr) => acc + curr, 0)
        );

        const SD = customRound(totalSumofSquare / countN);

        const SDSquareSIGMA = customRound(Math.sqrt(SD));
        setSdSquareSIGMA(SDSquareSIGMA);
    }, [meanData, countN]);

    const chartRefMean = useRef(null);

    useEffect(() => {
        if (!chartRefMean.current) return;
        const myChart = echarts.init(chartRefMean.current);
        if (meanData.length === 0) {
            myChart.setOption({
                title: {
                    text: "No data available",
                    left: "center",
                    top: "middle",
                    textStyle: {
                        fontSize: 18,
                        lineHeight: 28,          // 1.75rem = 28px (optional in ECharts)
                        fontWeight: 450,
                        color: "#ef4444", //text-[#ef4450]
                    },
                },
            });
            return;
        }

        const option = {
            grid: {
                // Give the spline a bit more breathing room so the smooth curve
                // never renders outside the card on the left/right edges.
                left: 24,
                right: 24,
                top: 12,
                bottom: 0,
                // No axis labels are shown, so don't reserve extra space.
                containLabel: false,
            },
            title: { show: false },

            tooltip: {
                trigger: "axis", // Shows tooltip when hovering anywhere near the line
                axisPointer: {
                    type: "none",
                    show: false,
                },
                show: true,
                formatter: function (params) {
                    const index = params[0].dataIndex; // Get the index of hovered point
                    const labels = [
                        " ",
                        " ",
                        " ",
                        " ",
                        " ",
                        " ",
                        " ",
                    ];
                    return `<b>${labels[index]}  </b> ${USAComma(
                        resultArray[index]
                    )}`; // Show mapped label and actual value
                },
            },

            xAxis: {
                grid: { display: false },
                type: "category",
                boundaryGap: false,
                axisLabel: { show: false }, //  Hides the x-axis labels (1,2,3,4)
                axisTick: { show: false }, //  Hides the small vertical tick marks
                axisLine: { show: false },
            },
            yAxis: {
                show: true,
                grid: {
                    display: false,
                },
                min: 0,
                axisLabel: {
                    show: false,
                },
                splitLine: { show: false }, // ✅ This removes the horizontal grid lines
            },
            series: [
                {
                    name: "Leads",
                    data: [0, 30, 80, 150, 80, 30, 0],
                    type: "line",
                    smooth: true,
                    clip: true,
                    symbol: "none",
                    symbolSize: 1,
                    lineStyle: {
                        color: "#2E7D32",
                        width: 2, // Adjust thickness if needed
                    },
                    areaStyle: {
                        // Bell curve gradient: light at bottom → darker at peak (top)
                        origin: "start",
                        color: new echarts.graphic.LinearGradient(0, 1, 0, 0, [
                            { offset: 0, color: "rgba(168, 230, 163, 0.45)" }, // #A8E6A3 (more opaque at baseline)
                            { offset: 1, color: "rgba(46, 125, 50, 0.70)" },   // #2E7D32
                        ]),
                    },
                    // markLine: {
                    //     silent: true,
                    //     symbol: "none", // No arrow symbols
                    //     lineStyle: {
                    //         color: "white",
                    //         type: "solid",
                    //         width: 0.5, // Adjust line width
                    //     },
                    //     label: {
                    //         show: false, // Hide labels
                    //     },
                    //     data: [
                    //         { xAxis: 1 }, // Line at 30
                    //         { xAxis: 2 }, // Line at 80
                    //         { xAxis: 3 }, // Line at 150
                    //         { xAxis: 4 }, // Line at 80
                    //         { xAxis: 5 }, // Line at 30
                    //     ],
                    // },
                },
            ],
        };

        myChart.setOption(option);

        return () => {
            myChart.dispose();
        };
    }, [meanData, resultArray, showgraph, currency, USAComma]);

    return (
        <div className="relative isolate z-0 flex h-full min-h-0 w-full min-w-0 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
            <div className={`${showgraph ? "" : "rounded-lg"} h-12 rounded-tl-lg rounded-tr-lg px-2 pt-3`}>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={handleToggleTable}
                        className="flex shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white p-1.5 transition hover:bg-gray-50"
                        aria-label={showgraph ? "Collapse" : "Expand"}
                        aria-expanded={showgraph}
                    >
                        <ChevronDown
                            className={`h-4 w-4 text-gray-600 ${showgraph ? "" : "rotate-180"}`}
                            strokeWidth={2}
                        />
                    </button>
                    <h1 className="dashboard-card-title min-w-0 truncate">
                        REVENUE DISTRIBUTION
                    </h1>
                </div>
            </div>
            {showgraph && canShowGraphs && (
                <div className="flex min-h-0 flex-1 flex-col px-2 pb-2 rounded-b-md">
                    <div
                        style={{
                            boxShadow: "rgba(0, 0, 0, 0.05) 0px 6px 24px 0px, rgba(0, 0, 0, 0.08) 0px 0px 0px 1px"
                        }}
                        className="relative flex min-h-0 flex-[2.75] flex-col px-2 pb-0 rounded-md"
                    >
                        {(userLevel === "L0" || hasNoFeatureConfig || !currentEmployee || hasViewOrFullAccess(currentEmployee, "REVENUE DISTRIBUTION FILTER")) && (
                            <div className="flex items-start justify-between gap-3 px-2 pt-2">
                                <div className="flex w-44 flex-col gap-1">
                                    <label className="block text-xs font-semibold pr-2 text-[#1F2937]" htmlFor="Service">
                                        Status
                                    </label>
                                    <div className="relative border w-36 border-[#e3eaff] rounded-md bg-white">
                                        <div
                                            ref={dropdownRefService}
                                            className="relative p-0 rounded-xl align-middle items-center"
                                        >
                                            <ExpandMoreIcon
                                                className="absolute right-0 cursor-pointer pt-2 bg-white"
                                                onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                                                className="flex justify-start items-center align-middle w-full h-full p-2 text-nowrap truncate focus:outline-none text-xs font-semibold text-[#1F2937]"
                                            >
                                                {TypeStatus ? TypeStatus : <p className="text-xs font-semibold">All Status</p>}
                                            </button>
                                            {showStatusDropdown && (
                                                <div
                                                    className="absolute left-0 top-full z-40 flex max-h-36 w-full cursor-pointer flex-col gap-1 overflow-y-auto border border-gray-300 bg-white p-1 text-sm shadow-lg"
                                                >
                                                    <div
                                                        className="p-1 hover:bg-gray-200 cursor-pointer text-xs font-semibold"
                                                        onClick={() => handleStatusChange("All Status")}
                                                    >
                                                        All Status
                                                    </div>
                                                    {statusTypes.map((statusTypes) => (
                                                        <div
                                                            className="p-1 hover:bg-gray-200 cursor-pointer text-xs font-semibold"
                                                            onClick={() => handleStatusChange(statusTypes.status)}
                                                            key={statusTypes.id}
                                                            value={statusTypes.status}
                                                        >
                                                            {statusTypes.status}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    {showStatusTypeMessage && meanData.length === 0 && (
                                        <p className=" text-xs pl-2 ">Select Status</p>
                                    )}
                                </div>

                                <div
                                     className="shrink-0 rounded-md px-4 py-2 text-sm font-semibold"
                                     style={{ backgroundColor: "#E6F4EA", color: "#1B7F3B" }}>
                                    MEAN {currency}{USAComma(resultArray?.[3] ?? 0)}
                                </div>
                            </div>
                        )}
                        <div
                            className="relative flex min-h-0 w-full flex-1 flex-col overflow-hidden"
                            style={{
                                borderRadius: 12,
                            }}
                        >
                            <div
                                ref={chartRefMean}
                                style={{ width: "100%", height: "100%", overflow: "hidden" }}
                                className="min-h-0 min-w-0"
                            />
                        </div>
                    </div>
                    <div className="min-h-0 flex-[3] overflow-hidden border-t border-gray-100 pt-0">
                        <Sales showgraph={showgraph} filteredReports={localFilteredReports} isDownloadViewOnly={isDownloadViewOnly} />
                    </div>
                </div>

            )}
        </div>

    )
}

export default RevenueDistribution